// frontend/src/components/Provider/ProviderQuickActions.tsx
// UPDATED: Updated route references to match new navigation structure

import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  Wrench,
  MapPin,
  User,
  Briefcase
} from 'lucide-react';

interface QuickAction {
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  iconBg: string;
  iconColor: string;
  hoverBg: string;
  to: string;
}

interface ProviderQuickActionsProps {
  showProfileAction?: boolean;
  title?: string;
  description?: string;
}

const ProviderQuickActions: React.FC<ProviderQuickActionsProps> = ({
  showProfileAction = false,
  title = "Quick Setup",
  description
}) => {
  const baseActions: QuickAction[] = [
    {
      title: "Manage Services",
      description: "Add and configure the services you offer",
      icon: Wrench,
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      hoverBg: "group-hover:bg-blue-200",
      to: "/provider-services"
    },
    {
      title: "Service Areas",
      description: "Set up your coverage areas and locations",
      icon: MapPin,
      iconBg: "bg-green-100",
      iconColor: "text-green-600",
      hoverBg: "group-hover:bg-green-200",
      to: "/provider-areas"
    }
  ];

  const businessProfileAction: QuickAction = {
    title: "Edit Business Profile",
    description: "Update your business information and details",
    icon: Briefcase, // UPDATED: Changed from User to Briefcase
    iconBg: "bg-purple-100",
    iconColor: "text-purple-600",
    hoverBg: "group-hover:bg-purple-200",
    to: "/business-profile" // UPDATED: Changed from /provider/profile to /business-profile
  };

  const myProfilesAction: QuickAction = {
    title: "My Profiles", // UPDATED: Changed from "Account Settings" to "My Profiles"
    description: "Manage your personal account and preferences", // UPDATED: Updated description
    icon: User, // UPDATED: Changed from Settings to User
    iconBg: "bg-gray-100",
    iconColor: "text-gray-600",
    hoverBg: "group-hover:bg-gray-200",
    to: "/profile" // UPDATED: Changed from /provider/settings to /profile
  };

  const actions = showProfileAction 
    ? [businessProfileAction, ...baseActions, myProfilesAction]
    : baseActions;

  return (
    <div className="mb-8">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        {description && (
          <p className="text-gray-600 mt-1">{description}</p>
        )}
      </div>
      
      <div className={`grid grid-cols-1 ${actions.length === 2 ? 'md:grid-cols-2' : actions.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4'} gap-4`}>
        {actions.map((action, index) => (
          <Link
            key={index}
            to={action.to}
            className="group bg-white rounded-lg border border-gray-200 p-6 hover:border-gray-300 transition-all duration-200 hover:shadow-md"
          >
            <div className="flex items-start">
              <div className={`flex-shrink-0 p-3 rounded-lg ${action.iconBg} ${action.hoverBg} transition-colors duration-200`}>
                <action.icon className={`h-6 w-6 ${action.iconColor}`} />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-gray-900 group-hover:text-gray-700">
                  {action.title}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {action.description}
                </p>
                <div className="flex items-center mt-3 text-sm text-blue-600 group-hover:text-blue-700">
                  <span>Configure</span>
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default ProviderQuickActions;